import { useState, useEffect, useCallback } from 'react';
import { QuestionnaireResponse, QuestionnaireStructure, AIAnalysisResponse, AIAnalysisResult, AdditionalQuestionnaire } from './types';

export const useQuestionnaire = (client: any) => {
  const [questionnaireResponses, setQuestionnaireResponses] = useState<QuestionnaireResponse[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [questionnaireStructures, setQuestionnaireStructures] = useState<Map<string, QuestionnaireStructure>>(new Map());
  const [loadingStructure, setLoadingStructure] = useState(false);

  const fetchQuestionnaireStructures = useCallback(async (questionnaireIds: string[]) => {
    const uniqueIds = questionnaireIds.filter((id, index, arr) => arr.indexOf(id) === index);
    if (uniqueIds.length === 0) return;

    console.log('[useQuestionnaire] Fetching questionnaire structures for:', uniqueIds);
    setLoadingStructure(true);

    try {
      const API_URL = process.env.REACT_APP_QUESTIONNAIRE_API;
      const API_KEY = process.env.REACT_APP_QUESTIONNAIRE_API_KEY;

      if (!API_KEY) {
        throw new Error('API_KEY is not configured');
      }

      const structurePromises = uniqueIds.map(async (id) => {
        if (questionnaireStructures.has(id)) return null;

        const response = await fetch(`${API_URL}/api/v1/questionnaires/${id}`, {
          method: "GET",
          mode: "cors",
          headers: {
            'Authorization': API_KEY,
            'Content-Type': 'application/json',
            'Accept': 'application/json',
          },
        });

        if (!response.ok) {
          console.error(`HTTP error! status: ${response.status} for ID ${id}`);
          return null; // Don't throw, allow other structures to load
        }

        const data: QuestionnaireStructure = await response.json();
        return { id, data };
      });

      const results = await Promise.all(structurePromises);

      setQuestionnaireStructures(prevStructures => {
        const newStructures = new Map(prevStructures);
        results.forEach(result => {
          if (result) {
            newStructures.set(result.id, result.data);
          }
        });
        console.log('[useQuestionnaire] Questionnaire structures fetched:', newStructures);
        return newStructures;
      });

    } catch (err: any) {
      console.error('[useQuestionnaire] Failed to fetch questionnaire structures:', err);
    } finally {
      setLoadingStructure(false);
    }
  }, [questionnaireStructures]);

  const fetchQuestionnaireResponses = useCallback(async () => {
    if (!client?.id) {
      console.error('[useQuestionnaire] Client ID is missing:', client);
      setError('Client ID is required to fetch questionnaire responses');
      return;
    }

    console.log('[useQuestionnaire] Starting API call for client:', client.id);
    setLoading(true);
    setError(null);

    try {
      const API_URL = process.env.REACT_APP_QUESTIONNAIRE_API;
      const API_KEY = process.env.REACT_APP_QUESTIONNAIRE_API_KEY;

      if (!API_KEY) {
        throw new Error('API_KEY is not configured');
      }

      const response = await fetch(`${API_URL}/api/v1/questionnaire-responses/user/${client.id}`, {
        method: "GET",
        mode: "cors",
        headers: {
          'Authorization': API_KEY,
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data: QuestionnaireResponse[] = await response.json();
      console.log('[useQuestionnaire] Existing responses fetched:', data);
      setQuestionnaireResponses(data);

      if (data && data.length > 0) {
        const questionnaireIds = data.map(res => res.questionnaireId);
        await fetchQuestionnaireStructures(questionnaireIds);
      }
    } catch (err: any) {
      console.error('[useQuestionnaire] Failed to fetch existing responses:', err);
      const errorMessage = err.message || 'Failed to fetch existing responses';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [client, fetchQuestionnaireStructures]);

  useEffect(() => {
    if (client?.id) {
      fetchQuestionnaireResponses();
    }
  }, [client, fetchQuestionnaireResponses]);

  return {
    questionnaireResponses,
    loading,
    error,
    questionnaireStructures,
    loadingStructure,
    fetchQuestionnaireResponses,
  };
};

export const useAIAnalysis = (client: any) => {
  const [aiAnalysis, setAiAnalysis] = useState<AIAnalysisResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [additionalQuestionnaires, setAdditionalQuestionnaires] = useState<AdditionalQuestionnaire[]>([]);

  const fetchAiAnalysis = useCallback(async (clientName: string) => {
    console.log('[useAIAnalysis] Fetching AI analysis for client:', clientName);
    setLoading(true);
    setError(null);

    try {
      const API_URL = process.env.REACT_APP_QUESTIONNAIRE_API;
      const API_KEY = process.env.REACT_APP_QUESTIONNAIRE_API_KEY;

      if (!API_KEY) {
        throw new Error('API_KEY is not configured');
      }

      const response = await fetch(`${API_URL}/api/v1/ai-tool-daisy/analysis/patient/${encodeURIComponent(clientName)}`, {
        method: "GET",
        mode: "cors",
        headers: {
          'Authorization': API_KEY,
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data: AIAnalysisResponse[] = await response.json();
      console.log('[useAIAnalysis] AI analysis fetched:', data);

      if (data && data.length > 0) {
        // Sort by createdAt to get the latest
        const sortedData = [...data].sort((a, b) => 
          new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
        );
        
        // Get the latest result
        const latestResult = sortedData[0];
        
        // Parse the resultJson string to proper JSON structure
        const parsedResult: AIAnalysisResult = JSON.parse(latestResult.resultJson);
        console.log('[useAIAnalysis] Parsed AI analysis result:', parsedResult);
        
        setAiAnalysis(parsedResult);

        // Extract additional questionnaires from B1, B2, B3
        const questionnaires: AdditionalQuestionnaire[] = [];

        // Add B1 questionnaires (Ethos Scan)
        if (parsedResult.B1 && Array.isArray(parsedResult.B1)) {
          parsedResult.B1.forEach(item => {
            if (item.ID) {
              questionnaires.push({
                id: item.ID,
                label: item.label,
                category: 'ethos',
                nr: item.nr,
              });
            }
          });
        }

        // Add B2 questionnaires (Recommended)
        if (parsedResult.B2 && Array.isArray(parsedResult.B2)) {
          parsedResult.B2.forEach(item => {
            if (item.ID) {
              questionnaires.push({
                id: item.ID,
                label: item.label,
                category: 'recommended',
                nr: item.nr,
              });
            }
          });
        }

        // Add B3 questionnaires (HETA)
        if (parsedResult.B3 && Array.isArray(parsedResult.B3)) {
          parsedResult.B3.forEach(item => {
            if (item.heta_id) {
              questionnaires.push({
                id: item.heta_id,
                label: item.label,
                category: 'heta',
                nr: item.nr,
                tier: item.tier,
                source_ie: item.source_ie,
              });
            }
          });
        }

        setAdditionalQuestionnaires(questionnaires);
        console.log('[useAIAnalysis] Extracted questionnaires:', questionnaires);
      } else {
        setAiAnalysis(null);
        setAdditionalQuestionnaires([]);
      }
    } catch (err: any) {
      console.error('[useAIAnalysis] Failed to fetch AI analysis:', err);
      const errorMessage = err.message || 'Failed to fetch AI analysis';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (client?.firstName && client?.familyName) {
      const clientName = `${client.firstName} ${client.familyName}`;
      fetchAiAnalysis(clientName);
    }
  }, [client, fetchAiAnalysis]);

  return {
    aiAnalysis,
    loading,
    error,
    additionalQuestionnaires,
    fetchAiAnalysis,
  };
};